/**
 * AttachmentTab — Inline attachment viewer for the split-screen layout.
 * Renders PDFs in an iframe, images as <img>, and a download prompt for other types.
 *
 * Beside an order this IS the PO reader (kanban fe62ebea): the document sits
 * next to the order fields, so checking one against the other needs no modal
 * and nothing to close. "Full screen" hands the rare long read to the shared
 * AttachmentPreviewOverlay.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { AttachmentDownloader, AttachmentMeta } from '../../types/portal';
import { portalService } from '../../services/portal';
import { formatFileSize } from '../../utils/dateFormatters';
import AttachmentPreviewOverlay from './AttachmentPreviewOverlay';
import { downloadBlobUrl, isPdfAttachment, isPreviewableAttachment } from './attachmentPreview';
import styles from './AttachmentTab.module.css';

interface AttachmentTabProps {
  attachment: AttachmentMeta;
  emailId: string;
  /** Override the byte fetch; defaults to the orders-portal path. */
  download?: AttachmentDownloader;
}

const _portalDownload: AttachmentDownloader = (att, emailId) =>
  portalService.downloadAttachment(emailId, att.filename);

const AttachmentTab: React.FC<AttachmentTabProps> = ({
  attachment,
  emailId,
  download = _portalDownload,
}) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const blobUrlRef = useRef<string | null>(null);

  // Fetch blob on mount for previewable types
  useEffect(() => {
    if (!isPreviewableAttachment(attachment.mime_type, attachment.filename)) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setExpanded(false);

    download(attachment, emailId)
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load attachment:', err);
        setError('Failed to load attachment');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    // `attachment` identity changes on every parent render; key on its fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, attachment.filename, attachment.mime_type, download]);

  const handleDownload = useCallback(async () => {
    try {
      let url = blobUrl;
      if (!url) {
        const blob = await download(attachment, emailId);
        url = URL.createObjectURL(blob);
      }
      downloadBlobUrl(url, attachment.filename);
      if (url !== blobUrl) URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [emailId, attachment, blobUrl, download]);

  const closeExpanded = useCallback(() => setExpanded(false), []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading {attachment.filename}...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.fallback}>
          <div className={styles.fallbackIcon}>!</div>
          <div className={styles.fallbackText}>{error}</div>
          <button
            className={styles.downloadBtn}
            onClick={handleDownload}
            type="button"
            data-action="attachment_download"
            data-target-email-id={emailId}
            data-outcome="request"
          >
            Download File
          </button>
        </div>
      </div>
    );
  }

  // Non-previewable: show download prompt
  if (!isPreviewableAttachment(attachment.mime_type, attachment.filename)) {
    const ext = attachment.filename.split('.').pop()?.toUpperCase() || '?';
    return (
      <div className={styles.container}>
        <div className={styles.fallback}>
          <div className={styles.fallbackIcon}>{ext}</div>
          <div className={styles.fallbackFilename}>{attachment.filename}</div>
          <div className={styles.fallbackMeta}>{formatFileSize(attachment.size)}</div>
          <button
            className={styles.downloadBtn}
            onClick={handleDownload}
            type="button"
            data-action="attachment_download"
            data-target-email-id={emailId}
            data-outcome="request"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>
          <div className={styles.fallbackHint}>
            This file type cannot be previewed inline
          </div>
        </div>
      </div>
    );
  }

  if (!blobUrl) return null;

  const isPdf = isPdfAttachment(attachment.mime_type, attachment.filename);
  const isImage = attachment.mime_type.startsWith('image/');
  if (!isPdf && !isImage) return null;

  return (
    <div className={styles.container} data-testid="attachment-tab">
      <div className={styles.previewHeader}>
        <span className={styles.previewFilename}>{attachment.filename}</span>
        <div className={styles.previewActions}>
          <button
            className={styles.downloadBtn}
            onClick={() => setExpanded(true)}
            type="button"
            title="Open full screen for a longer read"
            data-testid="attachment-expand"
            data-action="attachment_expand"
            data-target-email-id={emailId}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
              <line x1="21" y1="3" x2="14" y2="10" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
            Full screen
          </button>
          <button
            className={styles.downloadBtn}
            onClick={handleDownload}
            type="button"
            data-action="attachment_download"
            data-target-email-id={emailId}
            data-outcome="request"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>
        </div>
      </div>
      {isPdf ? (
        <iframe
          className={styles.pdfViewer}
          src={`${blobUrl}#toolbar=1&navpanes=0&view=FitH`}
          title={attachment.filename}
        />
      ) : (
        <div className={styles.imageContainer}>
          <img
            className={styles.imagePreview}
            src={blobUrl}
            alt={attachment.filename}
          />
        </div>
      )}
      {expanded && (
        <AttachmentPreviewOverlay
          blobUrl={blobUrl}
          filename={attachment.filename}
          mimeType={attachment.mime_type}
          emailId={emailId}
          onClose={closeExpanded}
        />
      )}
    </div>
  );
};

export default AttachmentTab;
